import { WarningTwoIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  InputGroup as ChakraInputGroup,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  HStack,
  InputRightElement,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { DateFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import { Hint } from 'BrokerTsLib/src/components/Hint'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { IsOtherNonregistered, IsTrailer } from 'BrokerTsLib/src/validation'
import { useEffect, useMemo } from 'react'

import { FormLabel } from '@/components/FormLabel'
import { InputGroup } from '@/components/InputGroup'
import { NumberInput } from '@/components/ui/NumberInput'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup as CustomRadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { Apps } from '@/dtos'
import { useInsurers, useSelections, useYearKm } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { LabelHintIcon } from '@/images/LabelHintIcon'

import { VehicleCategorySelection } from './VehicleCategorySelection'
import { VehicleIdentificationForm } from './VehicleIdentificationForm'
import { VehicleModelSelection } from './VehicleModelSelection'
import { VehiclePriceSelection } from './VehiclePriceSelection'


export const VehicleForm = () => {

  const { app, environment } = useApp()
  const { currentPartner } = usePartner()
  const { insuranceParamsId, isDisabled } = useInsuranceParams()

  const methods = useFormContext<InsuranceParamsDto>()
  const {
    formState: { errors },
    register,
    getValues,
    setValue,
    watch,
    control,
    trigger
  } = methods

  const isJapPoleasing = !!getValues('importedData')

  const category = watch('vehicle.druh')

  const puvod = watch('vehicle.puvod')
  const usage = watch('vehicle.usage')
  const manufacturerWarranty = watch('vehicle.manufacturerWarrantyType')
  const vehicleGaraged = watch('vehicle.vehicleGaraged')
  const smluvniServis = watch('vehicle.smluvniServis')
  const prefLikvidace = watch('vehicle.prefLikvidace')
  const autorizovanyServis = watch('vehicle.autorizovanyServis')
  const foreignRepairShop = watch('vehicle.foreignRepairShop')
  const isInProduction = !!watch('vehicle.isInProduction')

  const { data: dataVehicleYearKm } = useYearKm(category ?? 0, { enabled: !!category })
  const { data: dataPuvod } = useSelections(SelectionType.PuvodVozidla)
  const { data: dataUzemniPlatnost } = useSelections(SelectionType.UzemniPlatnost)
  const { data: dataZabezpeceni, isLoading: isLoadingZabezpeceni, isFetching: isFetchingZabezpeceni } = useSelections(SelectionType.Zabezpeceni)
  const { data: dataUsage, isLoading: isLoadingUsage, isFetching: isFetchingUsage } = useSelections(SelectionType.PouzitiVozidla)
  const { data: dataWarrantyLimitKm, isLoading: isLoadingWarrantyLimitKm, isFetching: isFetchingWarrantyLimitKm } = useSelections(SelectionType.WarrantyLimitKm)
  const { data: dataWarrantyMonthLimit, isLoading: isLoadingWarrantyMonthLimit, isFetching: isFetchingWarrantyMonthLimit } = useSelections(SelectionType.WarrantyMonthLimit)

  const { data: allInsurers } = useInsurers(false, false)
  const { data: allAddonInsurers } = useInsurers(true, false)

  const insurers = useMemo(() => allInsurers?.data.map(x => x.id), [allInsurers]) ?? []
  const addonInsurers = useMemo(() => allAddonInsurers?.data.map(x => x.id), [allAddonInsurers]) ?? []

  const isPrivesNaves = IsTrailer(category?.toString())
  const isOtherNonregistered = IsOtherNonregistered(category?.toString())
  useEffect(() => {
    if (isOtherNonregistered)
      setValue('vehicle.isInProduction', false)
  }, [isOtherNonregistered])

  const extraWizard = app == Apps.extra
  const shortInsurance = app == Apps.shortInsurance

  // nove umoznujeme pojistovat i jine pojistovny nez pouze subaru insurance. Proto nic na formulari neskryvam. V pripade reklamaci lze vratit zpet
  const hidePartsForSubaruInsurance = false
  // const hidePartsForSubaruInsurance = user?.subaruInsuranceAllowed && IsSubaruLessThan3YearsOld(make, firstRegDate)

  // pref likvidaci zobrazujeme jen do 24.5.2025
  const showPreferredLiquidation = environment == 'production' && new Date().getTime() < new Date(2025, 4, 24).getTime()

  const now = new Date()

  return (
    <>
      <FormBody>
        <VehicleIdentificationForm />

        <VehicleCategorySelection />

        <VehicleModelSelection isDisabled={isDisabled} />

        {shortInsurance &&
          <FormRow itemsCount={1}>
            <FormControl isInvalid={!!errors.vehicle?.vehicleTypeDescription}>
              <FormLabel>Bližší spec. vozidla</FormLabel>

              <InputGroup
                {...register('vehicle.vehicleTypeDescription')}
                isDisabled={isDisabled}
                sx={{ width: '100%' }}
              />

              {errors.vehicle?.vehicleTypeDescription && <FormErrorMessage>{errors.vehicle.vehicleTypeDescription.message}</FormErrorMessage>}
            </FormControl>
          </FormRow>
        }

        {!extraWizard && !shortInsurance &&
          <FormControl isInvalid={!!errors.vehicle?.uzemniPlatnost}>
            <FormLabel>Územní platnost</FormLabel>

            <Controller
              name="vehicle.uzemniPlatnost"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup onChange={e => onChange(parseInt(e))} value={value?.toString() ?? ''} isDisabled={isDisabled}>
                  <Stack direction='row' spacing={16}>
                    {dataUzemniPlatnost?.data.map((item) => (
                      <Radio value={item.id.toString()} key={item.id}>
                        {item.description}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
            />

            {errors.vehicle?.uzemniPlatnost && <FormErrorMessage>{errors.vehicle.uzemniPlatnost.message}</FormErrorMessage>}
          </FormControl>
        }

        {!extraWizard && !shortInsurance &&
          <FormControl isInvalid={!!errors.vehicle?.puvod}>
            <FormLabel>První registrace vozidla</FormLabel>

            <RadioGroup
              // {...register('vehicle.puvod')}
              value={puvod?.toString()}
              isDisabled={isDisabled}
              onChange={(value) => setValue('vehicle.puvod', parseInt(value))}
            >
              <Stack direction="row" spacing={16}>
                {dataPuvod?.data.map((item) => (
                  <Radio value={item.id.toString()} key={item.id}>
                    {item.description}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>

            {errors.vehicle?.puvod && <FormErrorMessage>{errors.vehicle?.puvod.message}</FormErrorMessage>}
          </FormControl>
        }

        <FormRow>
          <Stack gap={4}>
            <DateFormControl name='vehicle.firstRegistrationDate' label='Datum první registrace' control={control}
              isDisabled={isDisabled || isInProduction}
              isRequired={!isInProduction}
              maxDate={now}
              autoComplete={false}
              helperText={isOtherNonregistered &&
                <HStack>
                  <WarningTwoIcon color='orange.600' />
                  <Text fontWeight='semibold' fontSize='sm' color='orange.600'>Pro neregistrovaná vozidla uveďte datum pořízení</Text>
                </HStack>
              } />

            {!isOtherNonregistered &&
              <HStack gap={3}>
                <SwitchFormControl name='vehicle.isInProduction'
                  label='Dosud neregistrované vozidlo (nové)'
                  isDisabled={isDisabled}
                  onChange={(v) => { if (v) setValue('vehicle.firstRegistrationDate', null) }} />
                <Hint pl={4} maxW='40ch'>
                  <b>Vozidlo nebylo dosud registrované</b><br />
                  Například vozidlo ve výrobě nebo nové vozidlo uváděné do provozu.<br />
                  <Box my={2} />
                  Volba umožní pokračovat bez zadání data první registrace.
                </Hint>
              </HStack>
            }
          </Stack>

          {puvod && puvod === Selection.VehicleOriginForeign && !extraWizard && !shortInsurance &&
            <FormControl isInvalid={!!errors.vehicle?.firstRegistrationDateCz}>
              <FormLabel>
                Datum první registrace v ČR
              </FormLabel>

              <Datepicker
                isDisabled={isDisabled}
                name="vehicle.firstRegistrationDateCz"
              />
            </FormControl>
          }
        </FormRow>

        <FormRow>
          <FormControl isInvalid={!!errors.vehicle?.objem} isRequired>
            <FormLabel>Zdvihový objem</FormLabel>

            <ChakraInputGroup>
              <NumberInput {...register('vehicle.objem')} isDisabled={isDisabled} />
              <InputRightElement>cm³</InputRightElement>
            </ChakraInputGroup>

            <FormHelperText>
              Pro elektrická vozidla není objem povinný.
            </FormHelperText>

            {errors.vehicle?.objem && <FormErrorMessage>{errors.vehicle.objem.message}</FormErrorMessage>}
          </FormControl>

          <FormControl isInvalid={!!errors.vehicle?.celkovaHmotnost} isRequired>
            {!isOtherNonregistered &&
              <FormLabel>
                Největší technicky přípustná hmotnost
              </FormLabel>
            }
            {isOtherNonregistered &&
              <FormLabel>
                Provozní hmotnost
              </FormLabel>
            }
            <ChakraInputGroup>
              <NumberInput {...register('vehicle.celkovaHmotnost')} isDisabled={isDisabled} />
              <InputRightElement>kg</InputRightElement>
            </ChakraInputGroup>

            {errors.vehicle?.celkovaHmotnost && <FormErrorMessage>{errors.vehicle.celkovaHmotnost.message}</FormErrorMessage>}
          </FormControl>
        </FormRow>

        <FormRow>
          <FormControl isInvalid={!!errors.vehicle?.vykon1} isRequired>
            <FormLabel>Max. výkon</FormLabel>

            <ChakraInputGroup>
              <NumberInput {...register('vehicle.vykon1')} isDisabled={isDisabled} />
              <InputRightElement>kW</InputRightElement>
            </ChakraInputGroup>

            {errors.vehicle?.vykon1 && <FormErrorMessage>{errors.vehicle.vykon1.message}</FormErrorMessage>}
          </FormControl>

          {!shortInsurance &&
            <FormControl isInvalid={!!errors.vehicle?.vykon2}>
              <FormLabel>Max. výkon 2</FormLabel>

              <ChakraInputGroup>
                <NumberInput {...register('vehicle.vykon2')} isDisabled={isDisabled} />
                <InputRightElement>kW</InputRightElement>
              </ChakraInputGroup>

              <FormHelperText>
                Maximální výkon druhého motoru. Tuto položku zadávejte, pokud se
                jedná např. o hybridní pohon, jako max. výkon elektrického
                motoru.
              </FormHelperText>

              {errors.vehicle?.vykon2 && <FormErrorMessage>{errors.vehicle.vykon2.message}</FormErrorMessage>}
            </FormControl>
          }
        </FormRow>

        <FormRow >
          {!(isPrivesNaves || isOtherNonregistered) &&
            <FormControl isInvalid={!!errors.vehicle?.mileage} isRequired>
              <FormLabel>
                Stav tachometru
              </FormLabel>

              <ChakraInputGroup>
                <NumberInput
                  placeholder="Např. 150 000"
                  {...register('vehicle.mileage')}
                  isDisabled={isDisabled}
                />
                <InputRightElement>km</InputRightElement>
              </ChakraInputGroup>

              {currentPartner?.partner == 'Respect' &&
                <FormHelperText>
                  <Button as={'a'} target='_blank' href={'https://kontrolatachometru.cz/'} variant='link'>Km</Button>
                </FormHelperText>
              }

              {errors.vehicle?.mileage && <FormErrorMessage>{errors.vehicle.mileage.message}</FormErrorMessage>}

            </FormControl>
          }

          {!(isPrivesNaves || isOtherNonregistered) && !extraWizard && !shortInsurance &&
            <FormControl isInvalid={!!errors.vehicle?.pocetMistSezeni} isRequired>
              <FormLabel>
                Počet míst k sezení
              </FormLabel>

              <ChakraInputGroup>
                <NumberInput
                  placeholder="Např. 5"
                  {...register('vehicle.pocetMistSezeni')}
                  isDisabled={isDisabled}
                />
              </ChakraInputGroup>

              {errors.vehicle?.pocetMistSezeni && <FormErrorMessage>{errors.vehicle.pocetMistSezeni.message}</FormErrorMessage>}
            </FormControl>
          }
        </FormRow>

        {!hidePartsForSubaruInsurance && !isOtherNonregistered && !extraWizard && !shortInsurance &&
          (dataVehicleYearKm?.data.length ?? 0) > 0 &&
          <FormControl w='100%' isInvalid={!!errors.vehicle?.yearKm} isRequired={!isJapPoleasing}>
            <Stack direction={'row'}>
              <FormLabel>Roční nájezd</FormLabel>
            </Stack>

            <Box
              sx={{
                w: '100%',
                maxW: {
                  base: 'none',
                  md: 'calc(50% - 2rem)',
                },
              }}
            >
              {(dataVehicleYearKm?.data.length ?? 0) > 0 &&
                <Select {...register('vehicle.yearKm')} value={watch('vehicle.yearKm') ?? ''} isDisabled={isDisabled}>
                  <option disabled={!isJapPoleasing} value=''>Vyberte roční nájezd</option>
                  {dataVehicleYearKm?.data.map((item) => (
                    <option value={item.value?.toString() ?? ''} key={item.value}>{item.name}</option>
                  ))}
                </Select>
              }
            </Box>

            {errors.vehicle?.yearKm && <FormErrorMessage>{errors.vehicle.yearKm.message}</FormErrorMessage>}

            <FormHelperText>
              Vyberte předpokládaný roční nájezd vozidla. Pokud údaj neznáte vyberte možnost {currentPartner?.partner == 'Jap' ? '"nemám zájem"' : '"nechci uvést"'}.
            </FormHelperText>
          </FormControl>
        }

        <FormRow>
          {(!extraWizard || addonInsurers.findIndex(x => x == Insurer.Fortegra) >= 0) && !shortInsurance &&
            <FormControl isInvalid={!!errors.vehicle?.usage} isRequired>
              <FormLabel isLoading={isFetchingUsage || isLoadingUsage}>Použití</FormLabel>

              {(dataUsage?.data.length ?? 0) > 0 &&
                <Select
                  {...register('vehicle.usage')}
                  isDisabled={isDisabled}
                  onChange={(e) => setValue('vehicle.usage', parseInt(e.target.value))}
                  onBlur={() => trigger('vehicle.usage')}
                  value={usage?.toString() ?? ''}
                >
                  <option disabled value=''>Vyberte způsob použití</option>
                  {dataUsage?.data.map((item) => (
                    <option value={item.id.toString()} key={item.id}>
                      {item.description}
                    </option>
                  ))}
                </Select>
              }

              {errors.vehicle?.usage && <FormErrorMessage>{errors.vehicle.usage.message}</FormErrorMessage>}
            </FormControl>
          }

          {!isOtherNonregistered && addonInsurers.findIndex(x => x == Insurer.Fortegra || x == Insurer.Colonnade) >= 0 && !shortInsurance &&
            <FormControl isInvalid={!!errors.vehicle?.purchaseDate} isRequired={extraWizard}>
              <FormLabel htmlFor="vehicle.purchaseDate" tooltip={extraWizard ? null : 'Nutné pro kalkulace doplňkového připojištění.'}>
                Datum nákupu vozidla
              </FormLabel>

              <Datepicker
                isDisabled={isDisabled}
                name="vehicle.purchaseDate"
              />
            </FormControl>
          }
        </FormRow>

        {!isOtherNonregistered && addonInsurers.findIndex(x => x == Insurer.Fortegra || x == Insurer.Colonnade) >= 0 && !shortInsurance &&
          <>
            <Flex>
              <FormControl isInvalid={!!errors.vehicle?.manufacturerWarrantyType}>
                <Stack direction={'row'}>
                  <FormLabel tooltip={extraWizard ? null : 'Pouze pro kalkulace doplňkového pojištění prodloužené záruky.'}>Vozidlo v záruce</FormLabel>
                </Stack>

                <CustomRadioGroup
                  name="vehicle.manufacturerWarrantyType"
                  valueTransform={(value) => value?.toString() ?? '0'}
                  isDisabled={isDisabled}
                  onChange={(e) => {
                    setValue('vehicle.manufacturerWarrantyType', parseInt(e))

                    if (e == '1') {
                      if (!getValues('vehicle.warrantyStartDate'))
                        setValue('vehicle.warrantyStartDate', getValues('vehicle.firstRegistrationDate'))
                    }
                  }}>
                  <Radio value={'1'}>Ano</Radio>
                  <Radio value={'2'}>Ne</Radio>
                  <Radio value={'0'}>Nemám zájem</Radio>
                </CustomRadioGroup>

                {errors.vehicle?.manufacturerWarrantyType && <FormErrorMessage>{errors.vehicle.manufacturerWarrantyType.message}</FormErrorMessage>}
              </FormControl>
            </Flex>

            {manufacturerWarranty == 0 &&
              <Text variant="warning">Připojištění prodloužené záruky nebude kalkulováno</Text>
            }

            {manufacturerWarranty == 1 &&
              <>
                <FormRow>
                  <FormControl isInvalid={!!errors.vehicle?.warrantyStartDate} isRequired>
                    <FormLabel htmlFor="vehicle.warrantyStartDate">
                      Záruka od
                    </FormLabel>

                    <Datepicker
                      isDisabled={isDisabled}
                      name="vehicle.warrantyStartDate"
                    />
                  </FormControl>

                  <FormControl isInvalid={!!errors.vehicle?.warrantyTermLimit} isRequired>
                    <FormLabel htmlFor="vehicle.warrantyTermLimit">
                      Počet měsíců záruky
                    </FormLabel>
                    {(isFetchingWarrantyMonthLimit || isLoadingWarrantyMonthLimit) && <Spinner />}

                    {(dataWarrantyMonthLimit?.data.length ?? 0) > 0 &&
                      <Select id="vehicle.warrantyTermLimit" {...register('vehicle.warrantyTermLimit')} isDisabled={isDisabled}>
                        <option value='' disabled>Vyberte</option>
                        {dataWarrantyMonthLimit?.data.
                          sort((x, y) => (x.displayOrder ?? x.id) - (y.displayOrder ?? y.id)).
                          map((item) => (
                            <option value={item.value} key={item.id}>{item.description}</option>
                          ))}
                      </Select>
                    }
                  </FormControl>

                  <FormControl isInvalid={!!errors.vehicle?.warrantyKmLimit} isRequired>
                    <FormLabel htmlFor="vehicle.warrantyKmLimit">
                      Počet km záruky
                    </FormLabel>
                    {(isFetchingWarrantyLimitKm || isLoadingWarrantyLimitKm) && <Spinner />}

                    {(dataWarrantyLimitKm?.data.length ?? 0) > 0 &&
                      <Select id="vehicle.warrantyKmLimit" {...register('vehicle.warrantyKmLimit')} isDisabled={isDisabled}>
                        <option value='' disabled>Vyberte</option>
                        {dataWarrantyLimitKm?.data
                          .sort((x, y) => (x.displayOrder ?? x.id) - (y.displayOrder ?? y.id))
                          .filter(item => item.name != 'NU')
                          .map((item) => (
                            <option value={item.value} key={item.id}>{item.description}</option>
                          ))}
                      </Select>
                    }
                  </FormControl>
                </FormRow>
              </>
            }
          </>
        }

        {!extraWizard && !isOtherNonregistered && !shortInsurance &&
          <>
            <SimpleGrid columns={{ sm: 1, md: 1 }} spacingX='10' spacingY='4' w='100%'>
              {insurers.indexOf(Insurer.Pillow) >= 0 && !hidePartsForSubaruInsurance &&
                <FormControl isInvalid={!!errors.vehicle?.vehicleGaraged}>
                  <Checkbox
                    isChecked={vehicleGaraged}
                    isDisabled={isDisabled}
                    onChange={(e) => setValue('vehicle.vehicleGaraged', e.target.checked)}>
                    <Flex gap={2} alignItems='center'>
                      Parkuje v garáži
                      <Tooltip hasArrow label='Pouze pro kalkulace pojišťovny Pillow.' placement="top">
                        <Box sx={{ lineHeight: 0 }}>
                          <LabelHintIcon width={4} height={4} />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Checkbox>
                </FormControl>
              }

              {!hidePartsForSubaruInsurance &&
                <>
                  <FormControl isInvalid={!!errors.vehicle?.smluvniServis}>
                    <Checkbox
                      isChecked={smluvniServis ?? false}
                      isDisabled={isDisabled}
                      onChange={(e) => setValue('vehicle.smluvniServis', e.target.checked)}>
                      <Flex gap={2} alignItems='center'>
                        Souhlas s opravou ve sml. servisu
                        <Tooltip hasArrow label='Klient souhlasí s opravou ve smluvním servisu pojistitele, za což dostává slevu na pojistném. V případě porušení závazku opravovat ve smluvním servise může pojistitel krátit pojistné plnění nebo navýšit spoluúčast.' placement="top">
                          <Box sx={{ lineHeight: 0 }}>
                            <LabelHintIcon width={4} height={4} />
                          </Box>
                        </Tooltip>
                      </Flex>
                    </Checkbox>
                  </FormControl>

                  {showPreferredLiquidation &&
                    insurers.findIndex(x => x == Insurer.Kooperativa) >= 0 &&
                    <FormControl isInvalid={!!errors.vehicle?.prefLikvidace}>
                      <Checkbox
                        isChecked={prefLikvidace ?? false}
                        isDisabled={isDisabled}
                        onChange={(e) => setValue('vehicle.prefLikvidace', e.target.checked)}
                      >
                        <Flex gap={2} alignItems='center'>
                          {(currentPartner?.partner ?? '') != 'Jap' &&
                            <Text>Preferovaný způsob likvidace</Text>
                          }
                          {(currentPartner?.partner ?? '') == 'Jap' &&
                            <Text>Domluvený způsob opravy</Text>
                          }
                          <Tooltip hasArrow label='Pouze pro pojišťovnu Kooperativa: V případě, kdy vozidlo nebude opraveno v určeném servisu, pojistitel poskytne pojistné plnění ve výši nákladů na opravu stanovených rozpočtem. Plnění na základě rozpočtu může pojištěný vyžádat i u vozidla mladšího než 120 měsíců. V případě, kdy sklo nebude opravováno v servisu určeném pojistitelem způsobem, jaký pojistitel určil, stanoví pojistitel pojistné plnění rozpočtem.' placement="top">
                            <Box sx={{ lineHeight: 0 }}>
                              <LabelHintIcon width={4} height={4} />
                            </Box>
                          </Tooltip>
                        </Flex>
                      </Checkbox>
                    </FormControl>
                  }

                  {insurers.findIndex(x => x == Insurer.HvP || x == Insurer.Pillow) >= 0 &&
                    <FormControl isInvalid={!!errors.vehicle?.autorizovanyServis}>
                      <Checkbox
                        isChecked={autorizovanyServis ?? false}
                        isDisabled={isDisabled}
                        onChange={(e) => setValue('vehicle.autorizovanyServis', e.target.checked)}>
                        <Flex gap={2} alignItems='center'>
                          Autorizovaný servis
                          <Tooltip hasArrow label='Pouze pro pojišťovnu HVP a Pillow: Vozidlo bude opravováno v autorizovaném servise. Pro Pillow: Pokud je označena volba Ano, pak cena pojištění je vždy bez slevy na smluvní servisy, klient může využít jakýkoli autorizovaný servis a není vázaný se seznam smluvních servisů uvedeném na webu pojišťovny.' placement="top">
                            <Box sx={{ lineHeight: 0 }}>
                              <LabelHintIcon width={4} height={4} />
                            </Box>
                          </Tooltip>
                        </Flex>
                      </Checkbox>
                    </FormControl>
                  }

                  {insurers.findIndex(x => x == Insurer.Uniqa) >= 0 &&
                    (currentPartner?.partner ?? '') != 'Jap' &&
                    <FormControl isInvalid={!!errors.vehicle?.foreignRepairShop}>
                      <Checkbox
                        isChecked={foreignRepairShop ?? false}
                        isDisabled={isDisabled}
                        onChange={(e) => setValue('vehicle.foreignRepairShop', e.target.checked)}>
                        <Flex gap={2} alignItems='center'>
                          Limit ceny prací servisu ze zahraničí
                          <Tooltip hasArrow label='Pouze pro pojišťovnu UNIQA: Při zaškrtnutí je nastaven servis bez omezení, kde pojišťovna počítá s obvyklými náklady v daném místě a čase na území na kterém se opravuje, ČR, SR i zahraničí. Pokud není zaškrtnuto, pojišťovna počítá s obvyklými náklady ze servisů v ČR a SR. Pokud bude vozidlo opravováno v zahraničí, tak jsou použité náklady z ČR/SR a rozdíl doplácí pojištěný.' placement="top">
                            <Box sx={{ lineHeight: 0 }}>
                              <LabelHintIcon width={4} height={4} />
                            </Box>
                          </Tooltip>
                        </Flex>
                      </Checkbox>
                    </FormControl>
                  }
                </>
              }
            </SimpleGrid>

            {/* 
            <FormControl w='100%' isInvalid={!!errors.vehicle?.equipment}>
              <Stack direction={'row'}>
                <FormLabel htmlFor="vehicle.equipment">
                  Výbava vozidla
                </FormLabel>
                {(isFetchingVybava || isLoadingVybava) && <Spinner />}
              </Stack>

              <FormHelperText>
                Ovlivňuje cenu havarijního pojištění u Kooperativa pojišťovny.
              </FormHelperText>

              <Controller
                name="vehicle.equipment"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup onChange={values => onChange(values.map(val => parseInt(val.toString())))} value={value ?? []} isDisabled={isDisabled}>
                    <SimpleGrid columns={{ sm: 2, md: 3 }} spacingX='10' spacingY='4' w='100%'>
                      {dataVybava?.data
                        ?.filter(item => [`${Selection.AutTransmission}`, `${Selection.FourWheelDrive}`, `${Selection.ParkingAssistant}`].some(x => item.id.toString() === x) ?? true)
                        .map(item => (
                          <Checkbox value={item.id} key={item.id}>
                            {item.description}
                          </Checkbox>
                        ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                )}
              />
            </FormControl> 
            */}

            <FormControl w='100%' isInvalid={!!errors.vehicle?.security}>
              <Stack direction={'row'}>
                <FormLabel htmlFor="vehicle.security">Zabezpečení</FormLabel>
                {(isFetchingZabezpeceni || isLoadingZabezpeceni) && <Spinner />}
              </Stack>

              <Controller
                name="vehicle.security"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup onChange={values => onChange(values.map(val => parseInt(val.toString())))} value={value ?? []} isDisabled={isDisabled}>
                    <SimpleGrid columns={{ sm: 2, md: 3 }} spacingX='10' spacingY='4' w='100%'>
                      {dataZabezpeceni?.data.map((item) => (
                        <Checkbox value={item.id} key={item.id}>
                          {item.description}
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                )}
              />
            </FormControl>
          </>
        }

        {!shortInsurance &&
          <VehiclePriceSelection
            isDisabled={isDisabled}
            extraWizard={extraWizard}
            insuranceParamsId={insuranceParamsId}
          />
        }

        {
        /* zakomentováno - Kooperativa k 1.4. nestandardní výbavu zrušila */
        /* zatím funkční pouze pro JAP, kvůli specifickému ukládání a tomu, že ostatní partneři dosud neměli implementováno  */
        /* {currentPartner?.partner == 'Jap' &&  
          insurers.indexOf(Insurer.Kooperativa) >= 0 &&
          !extraWizard &&
          <ExtraEquipmentComponent />
        } */}

      </FormBody >
    </>
  )
}

